import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../components/common/Modal';
import {
  PermissionGroup,
  Permission,
  CreatePermissionGroupDto,
  UpdatePermissionGroupDto,
  permissionGroupsService,
} from '../../services/api/permissionGroups';
import { useToast } from '../../contexts/ToastContext';

interface PermissionGroupFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  group?: PermissionGroup | null;
}

const PermissionGroupFormModal: React.FC<PermissionGroupFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  group,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const isEditMode = !!group;

  const [loading, setLoading] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [permissionsByModule, setPermissionsByModule] = useState<Record<string, Permission[]>>({});
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    isActive: true,
    permissionIds: [] as number[],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load permissions on mount
  useEffect(() => {
    const loadPermissions = async () => {
      setLoadingPermissions(true);
      try {
        const data = await permissionGroupsService.getPermissionsByModule();
        setPermissionsByModule(data);
        // Expand all modules by default
        setExpandedModules(new Set(Object.keys(data)));
      } catch (err) {
        showError('Failed to load permissions');
      } finally {
        setLoadingPermissions(false);
      }
    };

    if (isOpen) {
      loadPermissions();
    }
  }, [isOpen]);

  // Set form data when editing
  useEffect(() => {
    if (group) {
      setFormData({
        code: group.code,
        name: group.name,
        description: group.description || '',
        isActive: group.isActive,
        permissionIds: group.permissions?.map((p) => p.id) || [],
      });
    } else {
      setFormData({
        code: '',
        name: '',
        description: '',
        isActive: true,
        permissionIds: [],
      });
    }
    setErrors({});
  }, [group, isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    } else if (!/^[a-z_]+$/.test(formData.code)) {
      newErrors.code = 'Code must be lowercase letters and underscores only';
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    try {
      if (isEditMode && group) {
        const updateData: UpdatePermissionGroupDto = {
          code: formData.code,
          name: formData.name,
          description: formData.description || undefined,
          isActive: formData.isActive,
          permissionIds: formData.permissionIds,
        };
        await permissionGroupsService.update(group.id, updateData);
        showSuccess('Permission group updated successfully');
      } else {
        const createData: CreatePermissionGroupDto = {
          code: formData.code,
          name: formData.name,
          description: formData.description || undefined,
          isActive: formData.isActive,
          permissionIds: formData.permissionIds,
        };
        await permissionGroupsService.create(createData);
        showSuccess('Permission group created successfully');
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Operation failed';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const toggleModule = (module: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(module)) {
        next.delete(module);
      } else {
        next.add(module);
      }
      return next;
    });
  };

  const togglePermission = (permissionId: number) => {
    setFormData((prev) => {
      const ids = new Set(prev.permissionIds);
      if (ids.has(permissionId)) {
        ids.delete(permissionId);
      } else {
        ids.add(permissionId);
      }
      return { ...prev, permissionIds: Array.from(ids) };
    });
  };

  const toggleModulePermissions = (module: string, checked: boolean) => {
    const modulePermissions = permissionsByModule[module] || [];
    const modulePermissionIds = modulePermissions.map((p) => p.id);

    setFormData((prev) => {
      const ids = new Set(prev.permissionIds);
      modulePermissionIds.forEach((id) => {
        if (checked) {
          ids.add(id);
        } else {
          ids.delete(id);
        }
      });
      return { ...prev, permissionIds: Array.from(ids) };
    });
  };

  const isModuleFullySelected = (module: string) => {
    const modulePermissions = permissionsByModule[module] || [];
    return modulePermissions.every((p) => formData.permissionIds.includes(p.id));
  };

  const isModulePartiallySelected = (module: string) => {
    const modulePermissions = permissionsByModule[module] || [];
    const selected = modulePermissions.filter((p) => formData.permissionIds.includes(p.id));
    return selected.length > 0 && selected.length < modulePermissions.length;
  };

  const formatModuleName = (module: string) => {
    return module.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? t('permissionGroups.edit', 'Edit Permission Group') : t('permissionGroups.create', 'Create Permission Group')}
      size="large"
    >
      {loadingPermissions ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>{t('common.loading')}</div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>{t('permissionGroups.code', 'Code')} *</label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                className={errors.code ? 'error' : ''}
                placeholder="e.g., hr_viewer"
                disabled={isEditMode && group?.isSystem}
              />
              {errors.code && <div className="form-error">{errors.code}</div>}
            </div>
            <div className="form-group">
              <label>{t('permissionGroups.name', 'Name')} *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? 'error' : ''}
                placeholder="e.g., HR Viewers"
              />
              {errors.name && <div className="form-error">{errors.name}</div>}
            </div>
          </div>

          <div className="form-group">
            <label>{t('permissionGroups.description', 'Description')}</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe what this group can do..."
            />
          </div>

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
              />
              {t('status.active', 'Active')}
            </label>
          </div>

          <div className="permissions-section">
            <h3>{t('permissionGroups.permissions', 'Permissions')} ({formData.permissionIds.length} selected)</h3>
            
            {Object.entries(permissionsByModule).map(([module, permissions]) => (
              <div key={module} className="module-group">
                <div className="module-header" onClick={() => toggleModule(module)}>
                  <label className="module-checkbox" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isModuleFullySelected(module)}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = isModulePartiallySelected(module);
                        }
                      }}
                      onChange={(e) => toggleModulePermissions(module, e.target.checked)}
                    />
                  </label>
                  <span className="module-name">{formatModuleName(module)}</span>
                  <span className="module-count">
                    ({permissions.filter((p) => formData.permissionIds.includes(p.id)).length}/{permissions.length})
                  </span>
                  <span className="module-toggle">
                    {expandedModules.has(module) ? '▼' : '▶'}
                  </span>
                </div>
                
                {expandedModules.has(module) && (
                  <div className="module-permissions">
                    {permissions.map((permission) => (
                      <div key={permission.id} className="permission-item">
                        <input
                          type="checkbox"
                          checked={formData.permissionIds.includes(permission.id)}
                          onChange={() => togglePermission(permission.id)}
                        />
                        <div className="permission-info">
                          <div className="permission-name">{permission.name}</div>
                          {permission.description && (
                            <div className="permission-description">{permission.description}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="form-actions">
            <button type="button" className="button button-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="button button-primary" disabled={loading}>
              {loading ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default PermissionGroupFormModal;
